// The component needs refactoring. It was built under time pressure and with minimal energy. It requires adding validation, splitting into smaller parts, better styling, and improved state management.

import React, { useEffect, useRef, useState } from 'react';

import {
  AddOrUpdateConsultantParameters,
  ConsultantDTO,
  cropConnectApiClient,
} from '../../services/cropConnectApiClient';
import {
  ActionButton,
  CellContent,
  EditInput,
  Header,
  PageWrapper,
  PhotoCell,
  PhotoCellContent,
  PhotoContainer,
  SearchButton,
  SearchInput,
  SearchLabel,
  SearchSection,
  SearchWrapper,
  StyledTable,
  TableCell,
  TableContainer,
  TableHeader,
  TableRow,
  UploadButton,
} from './styles';
import { useBpStore } from '../../stores/bpStore';

type EditingCells = {
  [key: string]: string;
};

const emptyConsultant: ConsultantDTO = {
  displayName: '',
  email: '',
  phone: '',
  mobile: '',
  description: '',
  image: '',
  sapUserId: '',
};

export const AdministrationContacts = () => {
  const { fetchBusinessPartnerMetadata, businessPartnerMetadata } =
    useBpStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const newConsultantFileInputRef = useRef<HTMLInputElement>(null);

  const [searchAgriCenterId, setSearchAgriCenterId] = useState<string>('');
  const [editingCells, setEditingCells] = useState<EditingCells>({});
  const [isAddingNewConsultant, setIsAddingNewConsultant] = useState(false);
  const [newConsultant, setNewConsultant] =
    useState<ConsultantDTO>(emptyConsultant);
  const [selectedContactForPhoto, setSelectedContactForPhoto] =
    useState<ConsultantDTO | null>(null);
  const [adminConsultants, setAdminConsultants] = useState<ConsultantDTO[]>([]);
  const [isAdminLoading, setIsAdminLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      await fetchBusinessPartnerMetadata();
      const currentAgricenterId =
        searchAgriCenterId || businessPartnerMetadata?.agriCenterNumber;
      if (currentAgricenterId) {
        await fetchAdminConsultants(currentAgricenterId);
      }
    };

    fetchData();
  }, [fetchBusinessPartnerMetadata]);

  const fetchAdminConsultants = async (searchId?: string) => {
    setIsAdminLoading(true);
    try {
      const agricenterId =
        searchId || businessPartnerMetadata?.agriCenterNumber;

      if (!agricenterId) {
        setAdminConsultants([]);
        return;
      }

      const response =
        await cropConnectApiClient.getConsultantsByAgriCenterAdmin(
          agricenterId
        );
      if (response.data?.consultants) {
        setAdminConsultants(response.data.consultants);
      }
    } catch (error) {
      console.error('Error fetching admin consultants:', error);
      setAdminConsultants([]);
    } finally {
      setIsAdminLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchBusinessPartnerMetadata();
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (businessPartnerMetadata?.agriCenterNumber) {
      const currentAgricenterId =
        searchAgriCenterId || businessPartnerMetadata.agriCenterNumber;
      fetchAdminConsultants(currentAgricenterId);
    }
  }, [businessPartnerMetadata]);

  const handleSearch = async () => {
    if (searchAgriCenterId) {
      await fetchAdminConsultants(searchAgriCenterId);
    } else {
      await fetchAdminConsultants();
    }
  };

  const updateConsultantData = async (consultantData: ConsultantDTO) => {
    try {
      const currentAgricenterId =
        searchAgriCenterId || businessPartnerMetadata?.agriCenterNumber || '';
      const updateData: AddOrUpdateConsultantParameters = {
        agricenterId: currentAgricenterId,
        sapUserId: consultantData.sapUserId,
        displayName: consultantData.displayName,
        email: consultantData.email,
        phone: consultantData.phone,
        mobile: consultantData.mobile,
        title: consultantData.description,
        image: consultantData.image,
      };

      await cropConnectApiClient.addOrUpdateConsultant(updateData);
      await fetchAdminConsultants(currentAgricenterId);
    } catch (error) {
      console.error('Error updating consultant:', error);
      throw error;
    }
  };

  const uploadPhoto = async (contact: ConsultantDTO, file: File) => {
    try {
      if (!contact.sapUserId) {
        throw new Error('Cannot upload photo without sapUserId');
      }

      const currentAgricenterId =
        searchAgriCenterId || businessPartnerMetadata?.agriCenterNumber || '';
      await cropConnectApiClient.uploadConsultantPicture(
        currentAgricenterId,
        contact.sapUserId,
        { data: file, fileName: file.name }
      );
      await fetchAdminConsultants(currentAgricenterId);
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  };

  const createNewConsultant = async (consultantData: ConsultantDTO) => {
    try {
      const currentAgricenterId =
        searchAgriCenterId || businessPartnerMetadata?.agriCenterNumber || '';
      const newConsultantData: AddOrUpdateConsultantParameters = {
        agricenterId: currentAgricenterId,
        displayName: consultantData.displayName,
        email: consultantData.email,
        phone: consultantData.phone,
        title: consultantData.description,
        image: consultantData.image,
        mobile: consultantData.mobile,
        sapUserId: consultantData.sapUserId,
      };

      await cropConnectApiClient.addOrUpdateConsultant(newConsultantData);
      await fetchAdminConsultants(currentAgricenterId);
    } catch (error) {
      console.error('Error creating consultant:', error);
      throw error;
    }
  };

  const getUniqueKey = (contact: ConsultantDTO) => {
    return `${contact.displayName}-${contact.email}`.replace(
      /[^a-zA-Z0-9]/g,
      '_'
    );
  };

  const getCellKey = (contact: ConsultantDTO, field: keyof ConsultantDTO) =>
    `${getUniqueKey(contact)}-${field}`;

  const handleEdit = (
    contact: ConsultantDTO,
    field: keyof ConsultantDTO,
    value: string
  ) => {
    const cellKey = getCellKey(contact, field);
    setEditingCells((prev) => ({
      ...prev,
      [cellKey]: value,
    }));
  };

  const handleSave = async (
    contact: ConsultantDTO,
    field: keyof ConsultantDTO
  ) => {
    const cellKey = getCellKey(contact, field);
    const newValue = editingCells[cellKey];

    try {
      await updateConsultantData({
        ...contact,
        [field]: newValue,
      });

      setEditingCells((prev) => {
        const newState = { ...prev };
        delete newState[cellKey];
        return newState;
      });
    } catch (error) {
      console.error('Error saving consultant:', error);
    }
  };

  const handleCancel = (contact: ConsultantDTO, field: keyof ConsultantDTO) => {
    const cellKey = getCellKey(contact, field);
    setEditingCells((prev) => {
      const newState = { ...prev };
      delete newState[cellKey];
      return newState;
    });
  };

  const handlePhotoUpload = (contact: ConsultantDTO) => {
    setSelectedContactForPhoto(contact);
    fileInputRef.current?.click();
  };

  const handlePhotoFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file && selectedContactForPhoto) {
      try {
        await uploadPhoto(selectedContactForPhoto, file);
      } catch (error) {
        console.error('Error uploading photo:', error);
      }
    }
    if (event.target) {
      event.target.value = '';
    }
    setSelectedContactForPhoto(null);
  };

  const handleNewConsultantPhotoUpload = () => {
    newConsultantFileInputRef.current?.click();
  };

  const handleNewConsultantPhotoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        const base64WithoutPrefix = base64String.split(',')[1];
        setNewConsultant((prev) => ({
          ...prev,
          image: base64WithoutPrefix,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteConsultant = async (contact: ConsultantDTO) => {
    if (window.confirm('Are you sure you want to delete this consultant?')) {
      try {
        const currentAgricenterId =
          searchAgriCenterId || businessPartnerMetadata?.agriCenterNumber;
        if (!currentAgricenterId) {
          console.error('Cannot delete consultant without agricenterId');
          return;
        }

        await cropConnectApiClient.deleteConsultant(
          contact.sapUserId!,
          currentAgricenterId
        );

        await fetchAdminConsultants(currentAgricenterId);
      } catch (error) {
        console.error('Error deleting consultant:', error);
      }
    }
  };

  const handleAddNewConsultant = () => {
    setIsAddingNewConsultant(true);
    setNewConsultant(emptyConsultant);
  };

  const handleSaveNewConsultant = async () => {
    try {
      await createNewConsultant(newConsultant);
      setIsAddingNewConsultant(false);
      setNewConsultant(emptyConsultant);
    } catch (error) {
      console.error('Error saving new consultant:', error);
    }
  };

  const handleCancelNewConsultant = () => {
    setIsAddingNewConsultant(false);
    setNewConsultant(emptyConsultant);
  };

  const getImageMimeType = (base64String: string): string => {
    const signatures = {
      '/9j/': 'image/jpeg',
      iVBORw0KGgo: 'image/png',
      R0lGOD: 'image/gif',
      UklGR: 'image/webp',
    };

    for (const [signature, mimeType] of Object.entries(signatures)) {
      if (base64String.startsWith(signature)) {
        return mimeType;
      }
    }

    return 'image/jpeg';
  };

  const renderCellContent = (
    contact: ConsultantDTO,
    field: keyof ConsultantDTO
  ) => {
    const cellKey = getCellKey(contact, field);
    const isEditing = cellKey in editingCells;
    const currentValue = isEditing
      ? editingCells[cellKey]
      : String(contact[field] || '');

    if (isEditing) {
      return (
        <CellContent>
          <EditInput
            value={currentValue}
            onChange={(e) =>
              setEditingCells((prev) => ({
                ...prev,
                [cellKey]: e.target.value,
              }))
            }
            autoFocus
          />
          <ActionButton onClick={() => handleSave(contact, field)}>
            Save
          </ActionButton>
          <ActionButton onClick={() => handleCancel(contact, field)}>
            Cancel
          </ActionButton>
        </CellContent>
      );
    }

    return (
      <CellContent>
        <span>{contact[field]}</span>
        <ActionButton
          onClick={() =>
            handleEdit(contact, field, String(contact[field] || ''))
          }
        >
          Edit
        </ActionButton>
      </CellContent>
    );
  };

  const renderNewConsultantRow = () => (
    <TableRow>
      <TableCell>
        <EditInput
          value={newConsultant.displayName || ''}
          onChange={(e) =>
            setNewConsultant((prev) => ({
              ...prev,
              displayName: e.target.value,
            }))
          }
          placeholder="Enter name"
        />
      </TableCell>
      <TableCell>
        <EditInput
          value={newConsultant.description || ''}
          onChange={(e) =>
            setNewConsultant((prev) => ({
              ...prev,
              description: e.target.value,
            }))
          }
          placeholder="Enter position"
        />
      </TableCell>
      <TableCell>
        <EditInput
          value={newConsultant.email || ''}
          onChange={(e) =>
            setNewConsultant((prev) => ({ ...prev, email: e.target.value }))
          }
          placeholder="Enter email"
        />
      </TableCell>
      <TableCell>
        <EditInput
          value={newConsultant.phone || ''}
          onChange={(e) =>
            setNewConsultant((prev) => ({ ...prev, phone: e.target.value }))
          }
          placeholder="Enter phone"
        />
      </TableCell>
      <TableCell>
        <EditInput
          value={newConsultant.mobile || ''}
          onChange={(e) =>
            setNewConsultant((prev) => ({ ...prev, mobile: e.target.value }))
          }
          placeholder="Enter mobile"
        />
      </TableCell>
      <TableCell>
        <EditInput
          value={newConsultant.sapUserId || ''}
          onChange={(e) =>
            setNewConsultant((prev) => ({ ...prev, sapUserId: e.target.value }))
          }
          placeholder="Enter SAP User ID"
        />
      </TableCell>
      <PhotoCell>
        <PhotoCellContent>
          <PhotoContainer>
            {newConsultant.image ? (
              <img
                src={`data:${getImageMimeType(newConsultant.image)};base64,${newConsultant.image}`}
                alt="New consultant"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = 'placeholder.png';
                }}
              />
            ) : (
              <span>No photo</span>
            )}
          </PhotoContainer>
          <UploadButton onClick={handleNewConsultantPhotoUpload}>
            Upload Photo
          </UploadButton>
        </PhotoCellContent>
      </PhotoCell>
      <TableCell>
        <ActionButton onClick={handleSaveNewConsultant}>Save</ActionButton>
        <ActionButton onClick={handleCancelNewConsultant}>Cancel</ActionButton>
      </TableCell>
    </TableRow>
  );

  return (
    <PageWrapper>
      <Header>
        <h1>Contacts Administration</h1>
        <ActionButton onClick={handleAddNewConsultant}>
          Add New Contact
        </ActionButton>
      </Header>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handlePhotoFileChange}
        accept="image/*"
      />

      <input
        type="file"
        ref={newConsultantFileInputRef}
        style={{ display: 'none' }}
        onChange={handleNewConsultantPhotoChange}
        accept="image/*"
      />

      <SearchSection>
        <SearchWrapper>
          <SearchLabel htmlFor="agriCenterId">AgriCenterID:</SearchLabel>
          <SearchInput
            id="agriCenterId"
            type="text"
            value={searchAgriCenterId}
            onChange={(e) => setSearchAgriCenterId(e.target.value)}
            placeholder="Enter AgriCenter ID"
          />
          <SearchButton onClick={handleSearch}>Search</SearchButton>
        </SearchWrapper>
      </SearchSection>

      <TableContainer>
        <StyledTable>
          <thead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Position</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Mobile</TableHeader>
              <TableHeader>SAP User ID</TableHeader>
              <TableHeader>Photo</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </thead>
          <tbody>
            {isAddingNewConsultant && renderNewConsultantRow()}
            {isAdminLoading ? (
              <TableRow>
                <TableCell colSpan={8}>Loading...</TableCell>
              </TableRow>
            ) : adminConsultants.length ? (
              adminConsultants.map((contact) => (
                <TableRow key={getUniqueKey(contact)}>
                  <TableCell>
                    {renderCellContent(contact, 'displayName')}
                  </TableCell>
                  <TableCell>
                    {renderCellContent(contact, 'description')}
                  </TableCell>
                  <TableCell>{renderCellContent(contact, 'email')}</TableCell>
                  <TableCell>{renderCellContent(contact, 'phone')}</TableCell>
                  <TableCell>{renderCellContent(contact, 'mobile')}</TableCell>
                  <TableCell>
                    {renderCellContent(contact, 'sapUserId')}
                  </TableCell>
                  <PhotoCell>
                    <PhotoCellContent>
                      <PhotoContainer>
                        {contact.image ? (
                          <img
                            src={`data:${getImageMimeType(contact.image)};base64,${contact.image}`}
                            alt={contact.displayName}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = 'placeholder.png';
                            }}
                          />
                        ) : (
                          <span>No photo</span>
                        )}
                      </PhotoContainer>
                      <UploadButton onClick={() => handlePhotoUpload(contact)}>
                        Upload Photo
                      </UploadButton>
                    </PhotoCellContent>
                  </PhotoCell>
                  <TableCell>
                    <ActionButton
                      onClick={() => handleDeleteConsultant(contact)}
                    >
                      Delete
                    </ActionButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8}>No contacts found</TableCell>
              </TableRow>
            )}
          </tbody>
        </StyledTable>
      </TableContainer>
    </PageWrapper>
  );
};

export default AdministrationContacts;
